/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import MenuItem from "@mui/material/MenuItem";
import { requiredValidator } from "@/src/services/validationRules";
import { useForm } from "@/src/provider/Form/FormProvider";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import Group from "@/src/components/Wizard/Group";
import Typography from "@mui/material/Typography";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import NumberFieldConfigured from "@/src/components/Form/ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";
import { Box } from "@mui/material";

type IncomeOtherProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function IncomeOther({ submitProps, product }: IncomeOtherProps) {
  const { values, handleSubmit } = useForm();
  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Einnahmen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasIncomeOther1"}
                question={"Haben Sie sonstige regelmäßige, unregelmäßige oder einmalige Einnahmen?"}
                modalContent={
                  <p>
                    Unter sonstige andere Einnahmen fallen zum Beispiel BaföG, jährliches Weihnachts- und Urlaubsgeld, Steuererstattungen, Betreuungsgeld und
                    vieles mehr. Auch Naturalleistungen wie freie Verpflegung oder sonstige Sachbezüge sind hier anzugeben.
                  </p>
                }
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasIncomeOther1 === "yes" && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>Bitte machen Sie hier genauere Angaben zu den Einnahmen.</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="incomeOther1Name"
                    label="Beschreibung der Einnahmen"
                    validators={[requiredValidator("Bitte geben Sie ein kurze Beschreibung an")]}
                  />
                </Grid>
                <NumberFieldConfigured caseData={product} fieldPath={"incomeOther1Amount"} required fieldType={"money"} numberType={"float"} />
                <Grid size={12}>
                  <ValidatorSelectField
                    label={"Wann erhalten Sie die Einnahmen?"}
                    name={`incomeOther1Period`}
                    validators={[requiredValidator("Bitte geben Sie den Zeitraum an")]}
                    MenuProps={{ slotProps: { paper: { style: { maxHeight: 200 } } } }}
                  >
                    <MenuItem value={"jährlich"}>jährlich</MenuItem>
                    <MenuItem value={"quartalsweise"}>quartalsweise</MenuItem>
                    <MenuItem value={"monatlich"}>monatlich</MenuItem>
                    <MenuItem value={"einmalig"}>einmalig</MenuItem>
                    <MenuItem value={"unregelmäßig"}>unregelmäßig</MenuItem>
                  </ValidatorSelectField>
                </Grid>
                <Grid size={12}>
                  <Group
                    type={"radio"}
                    name={"hasIncomeOther2"}
                    question={"Haben Sie noch weitere Einnahmen?"}
                    options={[
                      {
                        labelText: "Nein",
                        labelIcon: <CrossIcon />,
                        labelIconActive: <CrossIcon active />,
                        value: "no",
                      },
                      {
                        labelText: "Ja",
                        labelIcon: <CheckIcon />,
                        labelIconActive: <CheckIcon active />,
                        value: "yes",
                      },
                    ]}
                    validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
                  />
                </Grid>
                {values.hasIncomeOther2 === "yes" && (
                  <>
                    <Grid size={12}>
                      <ValidatorTextField
                        name="incomeOther2Name"
                        label="Beschreibung der Einnahmen"
                        validators={[requiredValidator("Bitte geben Sie ein kurze Beschreibung an")]}
                      />
                    </Grid>
                    <NumberFieldConfigured caseData={product} fieldPath={"incomeOther2Amount"} required fieldType={"money"} numberType={"float"} />

                    <Grid size={12}>
                      <ValidatorSelectField
                        label={"Wann erhalten Sie die Einnahmen?"}
                        name={`incomeOther2Period`}
                        validators={[requiredValidator("Bitte geben Sie den Zeitraum an")]}
                        MenuProps={{ slotProps: { paper: { style: { maxHeight: 200 } } } }}
                      >
                        <MenuItem value={"jährlich"}>jährlich</MenuItem>
                        <MenuItem value={"quartalsweise"}>quartalsweise</MenuItem>
                        <MenuItem value={"monatlich"}>monatlich</MenuItem>
                        <MenuItem value={"einmalig"}>einmalig</MenuItem>
                        <MenuItem value={"unregelmäßig"}>unregelmäßig</MenuItem>
                      </ValidatorSelectField>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
